import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Zap, Send, Users, Mail, X, CheckCircle, Play, Pause } from 'lucide-react';

const STATUS_COLORS = { new:'badge-gray', emailed:'badge-purple', replied:'badge-green', bounced:'badge-red' };
const EMAIL_STATUS = { sent:'badge-green', draft:'badge-gray', queued:'badge-amber', failed:'badge-red', opened:'badge-purple', replied:'badge-green' };
const scoreColor = s => s >= 70 ? 'var(--green)' : s >= 45 ? 'var(--amber)' : 'var(--red)';

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [emails, setEmails] = useState([]);
  const [tab, setTab] = useState('leads');
  const [loading, setLoading] = useState(true);
  const [composeModal, setComposeModal] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/campaigns/${id}`),
      api.get(`/leads?campaign_id=${id}`),
      api.get(`/emails?campaign_id=${id}`),
    ]).then(([c, l, e]) => {
      setCampaign(c.data.campaign);
      setLeads(l.data.leads || []);
      setEmails(e.data.emails || []);
    }).catch(() => toast.error('Failed to load campaign.'))
      .finally(() => setLoading(false));
  }, [id]);

  const toggleStatus = async () => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      await api.put(`/campaigns/${id}`, { status: newStatus });
      setCampaign(c => ({ ...c, status: newStatus }));
      toast.success(`Campaign ${newStatus}.`);
    } catch { toast.error('Failed to update.'); }
  };

  if (loading) return <div style={{ padding:32, display:'flex', alignItems:'center', gap:12, color:'var(--muted)' }}><div className="spinner" /> Loading...</div>;
  if (!campaign) return <div style={{ padding:32 }}><p>Campaign not found.</p><Link to="/campaigns" className="btn btn-secondary" style={{ marginTop:16, display:'inline-flex' }}>← Back</Link></div>;

  const openRate = campaign.emails_sent > 0 ? Math.round((campaign.emails_opened/campaign.emails_sent)*100) : 0;
  const replyRate = campaign.emails_sent > 0 ? Math.round((campaign.replies_received/campaign.emails_sent)*100) : 0;

  return (
    <div style={{ padding:32, maxWidth:1100 }}>
      <Link to="/campaigns" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:'var(--muted)', textDecoration:'none', marginBottom:20 }}>
        <ArrowLeft size={14} /> Back to Campaigns
      </Link>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:16 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <h1 style={{ fontSize:22, fontWeight:800, letterSpacing:'-.02em' }}>{campaign.name}</h1>
            <span className={`badge ${campaign.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{campaign.status}</span>
          </div>
          {campaign.subject_template && <p style={{ color:'var(--muted)', fontSize:13 }}>{campaign.subject_template}</p>}
        </div>
        <button className={`btn ${campaign.status === 'active' ? 'btn-secondary' : 'btn-primary'}`} onClick={toggleStatus}>
          {campaign.status === 'active' ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Activate</>}
        </button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
        {[
          { label:'Leads', value:leads.length, icon:Users, color:'#3b82f6' },
          { label:'Sent', value:campaign.emails_sent||0, icon:Send, color:'#7c3aed' },
          { label:'Open Rate', value:`${openRate}%`, icon:Mail, color:'#f59e0b' },
          { label:'Reply Rate', value:`${replyRate}%`, icon:CheckCircle, color:'#10b981' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="stat-card" style={{ padding:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <p style={{ fontSize:10.5, color:'var(--muted)', marginBottom:6 }}>{label}</p>
                <p style={{ fontSize:22, fontWeight:800 }}>{value}</p>
              </div>
              <Icon size={17} color={color} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)', marginBottom:20 }}>
        {['leads','emails'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding:'9px 16px', fontSize:13, fontWeight:500, background:'none', border:'none', cursor:'pointer', borderBottom:`2px solid ${tab===t?'var(--accent)':'transparent'}`, color:tab===t?'var(--text)':'var(--muted)', transition:'all .15s', textTransform:'capitalize', fontFamily:'inherit' }}>
            {t} ({t==='leads'?leads.length:emails.length})
          </button>
        ))}
      </div>

      {tab === 'leads' && (
        leads.length === 0 ? (
          <div className="empty" style={{ border:'1px solid var(--border)', borderRadius:12 }}>
            <Users size={28} style={{ margin:'0 auto 12px', opacity:.2, display:'block' }} />
            <p>No leads assigned to this campaign.</p>
            <Link to="/leads" className="btn btn-secondary btn-sm" style={{ marginTop:12, display:'inline-flex' }}>Go to Leads</Link>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Role</th><th>Score</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id}>
                    <td style={{ fontWeight:600 }}>{lead.name}</td>
                    <td style={{ color:'var(--muted)', fontSize:12 }}>{lead.email}</td>
                    <td style={{ color:'var(--muted)' }}>{lead.company||'—'}</td>
                    <td style={{ color:'var(--muted)' }}>{lead.role||'—'}</td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:40, height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
                          <div style={{ width:`${lead.score}%`, height:'100%', background:scoreColor(lead.score) }} />
                        </div>
                        <span style={{ fontSize:12, fontWeight:700, color:scoreColor(lead.score) }}>{lead.score}</span>
                      </div>
                    </td>
                    <td><span className={`badge ${STATUS_COLORS[lead.status]||'badge-gray'}`}>{lead.status}</span></td>
                    <td>
                      <button className="btn btn-primary btn-sm" onClick={() => setComposeModal({ lead })}
                        disabled={lead.status==='replied'||lead.status==='unsubscribed'}>
                        <Zap size={11} /> Generate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'emails' && (
        emails.length === 0 ? (
          <div className="empty" style={{ border:'1px solid var(--border)', borderRadius:12 }}>
            <Mail size={28} style={{ margin:'0 auto 12px', opacity:.2, display:'block' }} />
            <p>No emails sent yet.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>To</th><th>Subject</th><th>Type</th><th>Status</th><th>Sent</th></tr></thead>
              <tbody>
                {emails.map(email => (
                  <tr key={email.id}>
                    <td><div style={{ fontWeight:600 }}>{email.lead_name}</div><div style={{ fontSize:11, color:'var(--muted)' }}>{email.lead_email}</div></td>
                    <td style={{ maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--muted)' }}>{email.subject}</td>
                    <td>{email.is_follow_up ? <span className="badge badge-amber">Follow-up #{email.follow_up_number}</span> : <span className="badge badge-gray">Initial</span>}</td>
                    <td><span className={`badge ${EMAIL_STATUS[email.status]||'badge-gray'}`}>{email.status}</span></td>
                    <td style={{ color:'var(--muted)', fontSize:12 }}>{email.sent_at ? new Date(email.sent_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {composeModal && (
        <ComposeModal
          lead={composeModal.lead}
          campaignId={id}
          onClose={() => setComposeModal(null)}
          onSent={(emailData) => {
            setEmails(prev => [emailData, ...prev]);
            setLeads(prev => prev.map(l => l.id === composeModal.lead.id ? { ...l, status:'emailed' } : l));
            setComposeModal(null);
            setCampaign(c => ({ ...c, emails_sent: (c.emails_sent||0)+1 }));
          }}
        />
      )}
    </div>
  );
}

function ComposeModal({ lead, campaignId, onClose, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [generated, setGenerated] = useState(true);
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');

  const tones = [
    { id:'casual', label:'Casual', desc:'Friendly and conversational', emoji:'😊' },
    { id:'professional', label:'Professional', desc:'Polished and business-like', emoji:'💼' },
    { id:'direct', label:'Direct', desc:'Short and straight to point', emoji:'⚡' },
  ];

  const lengths = [
    { id:'short', label:'Short', desc:'Under 80 words', emoji:'⚡' },
    { id:'medium', label:'Medium', desc:'100-150 words', emoji:'✦' },
    { id:'detailed', label:'Detailed', desc:'150-200 words', emoji:'📝' },
  ];

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/emails/generate', {
        lead_id: lead.id,
        campaign_id: campaignId,
        custom_context: `Write this email in a ${tone} tone and keep it ${
          length === 'short' ? 'under 80 words' :
          length === 'medium' ? 'between 100-150 words' :
          'between 150-200 words with more detail'
        }. ${
          tone === 'casual' ? 'Be friendly, warm, and conversational. Avoid corporate language.' :
          tone === 'professional' ? 'Be polished, clear, and professional. Maintain business etiquette.' :
          'Be extremely direct and concise. Get to the point immediately.'
        }`
      });
      setSubject(res.data.subject);
      setBody(res.data.body);
      setGenerated(true);
      toast.success('Email generated!');
    } catch (err) { toast.error(err.response?.data?.error || 'AI generation failed.'); }
    finally { setGenerating(false); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body required.'); return; }
    setSending(true);
    try {
      const res = await api.post('/emails/send', { lead_id: lead.id, campaign_id: campaignId, subject, body });
      toast.success('Email sent!');
      if (res.data.free_emails_remaining !== null && res.data.free_emails_remaining <= 5) {
        toast(`⚠️ Only ${res.data.free_emails_remaining} free emails remaining. Upgrade to keep sending.`, { duration: 5000 });
      }
      onSent({ id: res.data.email_id, subject, body, status:'sent', lead_name: lead.name, lead_email: lead.email, sent_at: new Date().toISOString(), is_follow_up: false });
    } catch (err) {
      if (err.response?.data?.error === 'free_limit_reached') {
        toast.error('Free plan limit reached. Upgrade to keep sending.', { duration: 6000 });
        window.location.href = '/pricing';
        return;
      }
      toast.error(err.response?.data?.error || 'Send failed.');
    }
    finally { setSending(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:620 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
          <div>
            <h2 style={{ fontSize:17, fontWeight:700 }}>Compose Email</h2>
            <p style={{ fontSize:12, color:'var(--muted)', marginTop:3 }}>
              To: <strong>{lead.name}</strong> · {lead.email}{lead.company && ` · ${lead.company}`}
            </p>
          </div>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ marginBottom:14 }}>
          <p style={{ fontSize:12, fontWeight:600, color:'var(--muted)', marginBottom:8, textTransform:'uppercase', letterSpacing:'.04em' }}>Email Tone</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
            {tones.map(t => (
              <button key={t.id} onClick={() => setTone(t.id)} style={{
                padding:'10px 12px', borderRadius:9,
                border:`1.5px solid ${tone === t.id ? 'var(--accent)' : 'var(--border)'}`,
                background: tone === t.id ? 'var(--glow)' : 'var(--bg2)',
                cursor:'pointer', textAlign:'left', transition:'all .15s', fontFamily:'inherit'
              }}>
                <div style={{ fontSize:16, marginBottom:4 }}>{t.emoji}</div>
                <div style={{ fontSize:13, fontWeight:600, color: tone === t.id ? 'var(--accent)' : 'var(--text)' }}>{t.label}</div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <p style={{ fontSize:12, fontWeight:600, color:'var(--muted)', marginBottom:8, textTransform:'uppercase', letterSpacing:'.04em' }}>Email Length</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
            {lengths.map(l => (
              <button key={l.id} onClick={() => setLength(l.id)} style={{
                padding:'10px 12px', borderRadius:9,
                border:`1.5px solid ${length === l.id ? 'var(--accent)' : 'var(--border)'}`,
                background: length === l.id ? 'var(--glow)' : 'var(--bg2)',
                cursor:'pointer', textAlign:'left', transition:'all .15s', fontFamily:'inherit'
              }}>
                <div style={{ fontSize:16, marginBottom:4 }}>{l.emoji}</div>
                <div style={{ fontSize:13, fontWeight:600, color: length === l.id ? 'var(--accent)' : 'var(--text)' }}>{l.label}</div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{l.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <button className="btn btn-primary" onClick={generate} disabled={generating}
          style={{ width:'100%', justifyContent:'center', marginBottom:16 }}>
          {generating ? 'Generating...' : <><Zap size={13} /> Generate {tones.find(t=>t.id===tone)?.label} Email</>}
        </button>

        {generated && (
          <>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:5, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em' }}>Subject</label>
              <input className="input" value={subject} onChange={e => setSubject(e.target.value)} />
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:5, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em' }}>
                Body <span style={{ color:'var(--muted)', fontWeight:400, textTransform:'none' }}>(edit before sending)</span>
              </label>
              <textarea className="input" value={body} onChange={e => setBody(e.target.value)} style={{ minHeight:200, lineHeight:1.7 }} />
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={send} disabled={sending}>
                {sending ? 'Sending...' : <><Send size={13} /> Send Email</>}
              </button>
            </div>
          </>
        )}

        {!generated && (
          <div style={{ textAlign:'center', padding:'28px 20px', border:'1px dashed var(--border)', borderRadius:10, color:'var(--muted)' }}>
            <Zap size={22} style={{ margin:'0 auto 10px', display:'block', opacity:.3 }} />
            <p style={{ fontSize:13 }}>Select tone and length, then click Generate to create a personalized email for {lead.name}.</p>
          </div>
        )}
      </div>
    </div>
  );
}